import type { WhosOutTodayEntry } from '@hygge/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { PersonRow } from '../person-row';
import { ExpandableList } from '../expandable-list';
import { asArray, formatRange } from '../widget-format';

interface TeamTodayListProps {
  data: WhosOutTodayEntry[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Presentational body for the team-today widget — a capped, profile-linked
 * list of the caller's direct reports on leave today, with absence type + date range.
 * Same row treatment as `whosOutToday`.
 */
export function TeamTodayList({ data, isLoading, isError }: TeamTodayListProps) {
  const formatName = useEmployeeNameFormatter();

  // Sort by the displayed name so ordering honors the Name Display setting (matches whosOutToday).
  const rows = [...asArray(data)].sort((a, b) => formatName(a).localeCompare(formatName(b)));

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <p className="py-4 text-center text-xs text-muted-foreground">
        Couldn&apos;t load your team&apos;s absences.
      </p>
    );
  }
  if (rows.length === 0) {
    return <p className="py-4 text-center text-xs text-muted-foreground">Everyone&apos;s in today.</p>;
  }

  return (
    <ExpandableList
      items={rows}
      getKey={(p) => p.id}
      renderItem={(p: WhosOutTodayEntry) => (
        <PersonRow
          employee={p}
          subline={
            <span className="inline-flex items-center gap-1.5">
              {p.absenceTypeColor && (
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: p.absenceTypeColor }}
                />
              )}
              {p.absenceTypeName} · {formatRange(p.startDate, p.endDate)}
            </span>
          }
        />
      )}
    />
  );
}
